import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FolderKanban, Trash2, Eye, Pencil, Satellite, Database, Stethoscope, Search, Loader2, CheckCircle2, AlertTriangle, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BatchImportDialog } from "@/components/BatchImportDialog";
import api from "@/lib/api";
import { PageHeader, EmptyState, Disclaimer } from "@/components/common";
import { REGIMES, PHASES } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = {
    process_number: "", year: "", holder: "", document: "", substance: "", secondary_substances: "",
    municipality: "", uf: "", area_ha: "", regime: "", phase: "", title_number: "",
    publication_date: "", start_date: "", end_date: "", technical_responsible: "", crea: "", notes: "",
};

const fullNumber = (v) => {
    const n = (v.process_number || "").trim();
    if (/\/\s*\d{4}$/.test(n)) return n;
    return v.year?.trim()?.length === 4 ? `${n}/${v.year.trim()}` : "";
};

function useAnmLookup(value, onChange) {
    const [loading, setLoading] = useState(false);
    const [filled, setFilled] = useState("");
    const run = async (manual) => {
        const num = fullNumber(value);
        if (!num) { if (manual) toast.error("Informe o número e o ano do processo (ex.: 844030 / 2021)."); return; }
        if (!manual && num === filled) return;
        setLoading(true);
        try {
            const r = await api.get("/anm/lookup", { params: { numero: num } });
            if (r.data.status === "ok") {
                const fl = r.data.fields;
                onChange({ ...value, ...Object.fromEntries(Object.entries(fl).filter(([, v]) => v !== "" && v != null)) });
                setFilled(`${fl.process_number}/${fl.year}`);
                toast.success("Dados preenchidos pela ANM", { description: `Fonte: base oficial ANM (SCM)${r.data.fase_anm ? ` · Fase: ${r.data.fase_anm}` : ""}` });
            } else if (r.data.status === "baixando_base") {
                toast.info("Preparando a base da ANM", { description: r.data.detail });
            } else {
                toast.warning("Processo não localizado", { description: r.data.detail });
            }
        } catch (e) {
            if (manual) toast.error(e.response?.data?.detail || "Não foi possível consultar a ANM agora.");
        } finally { setLoading(false); }
    };
    return { loading, filled: filled && filled === fullNumber(value).replace(/\./g, ""), run };
}

const procKey = (n) => {
    const m = (n || "").replace(/\s+/g, "").match(/^([\d.]+)\/(\d{4})$/);
    return m ? `${parseInt(m[1].replace(/\./g, ""), 10)}/${m[2]}` : (n || "").trim();
};

function DuplicateNotice({ dup }) {
    const navigate = useNavigate();
    return (
        <div className="sm:col-span-2 flex items-center justify-between gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="duplicate-notice">
            <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> O processo {dup.process_number} já está cadastrado na sua carteira.</span>
            <button type="button" onClick={() => navigate(`/processos/${dup.id}`)} className="font-semibold underline" data-testid="duplicate-open-link">Abrir</button>
        </div>
    );
}

export function ProcessForm({ value, onChange, duplicate }) {
    const f = (k) => (e) => onChange({ ...value, [k]: e.target.value });
    const s = (k) => (v) => onChange({ ...value, [k]: v });
    const anm = useAnmLookup(value, onChange);
    return (
        <div className="grid sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
            <div><Label className="mb-1 block">Número do processo ANM *</Label><Input data-testid="process-number" value={value.process_number} onChange={f("process_number")} onBlur={() => anm.run(false)} placeholder="844030" /></div>
            <div>
                <Label className="mb-1 block">Ano de origem *</Label>
                <div className="flex gap-2">
                    <Input data-testid="process-year" value={value.year} onChange={f("year")} onBlur={() => anm.run(false)} placeholder="2021" maxLength={4} />
                    <Button type="button" variant="outline" onClick={() => anm.run(true)} disabled={anm.loading} data-testid="anm-lookup-btn" className="shrink-0">
                        {anm.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        <span className="ml-1">Buscar na ANM</span>
                    </Button>
                </div>
            </div>
            {duplicate && <DuplicateNotice dup={duplicate} />}
            {anm.filled && !duplicate && (
                <div className="sm:col-span-2 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="anm-filled-notice">
                    <CheckCircle2 className="h-4 w-4" /> Dados preenchidos automaticamente pela base oficial da ANM (SCM). Revise antes de salvar.
                </div>
            )}
            <div><Label className="mb-1 block">Titular</Label><Input data-testid="process-holder" value={value.holder} onChange={f("holder")} /></div>
            <div><Label className="mb-1 block">CPF/CNPJ</Label><Input value={value.document} onChange={f("document")} /></div>
            <div><Label className="mb-1 block">Substância principal</Label><Input data-testid="process-substance" value={value.substance} onChange={f("substance")} placeholder="OURO" /></div>
            <div><Label className="mb-1 block">Substâncias secundárias</Label><Input value={value.secondary_substances} onChange={f("secondary_substances")} /></div>
            <div><Label className="mb-1 block">Município</Label><Input data-testid="process-municipality" value={value.municipality} onChange={f("municipality")} /></div>
            <div><Label className="mb-1 block">UF</Label><Input data-testid="process-uf" value={value.uf} onChange={f("uf")} placeholder="SC" maxLength={2} /></div>
            <div><Label className="mb-1 block">Área (hectares)</Label><Input data-testid="process-area" type="number" value={value.area_ha} onChange={f("area_ha")} /></div>
            <div>
                <Label className="mb-1 block">Regime</Label>
                <Select value={value.regime} onValueChange={s("regime")}>
                    <SelectTrigger data-testid="process-regime"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{REGIMES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            <div>
                <Label className="mb-1 block">Fase atual</Label>
                <Select value={value.phase} onValueChange={s("phase")}>
                    <SelectTrigger data-testid="process-phase"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{PHASES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            <div><Label className="mb-1 block">Número do título</Label><Input value={value.title_number} onChange={f("title_number")} /></div>
            <div><Label className="mb-1 block">Data de publicação</Label><Input type="date" value={value.publication_date || ""} onChange={f("publication_date")} /></div>
            <div><Label className="mb-1 block">Data inicial</Label><Input type="date" value={value.start_date || ""} onChange={f("start_date")} /></div>
            <div><Label className="mb-1 block">Data final/vencimento</Label><Input type="date" value={value.end_date || ""} onChange={f("end_date")} /></div>
            <div><Label className="mb-1 block">Responsável técnico</Label><Input value={value.technical_responsible} onChange={f("technical_responsible")} /></div>
            <div><Label className="mb-1 block">CREA</Label><Input value={value.crea} onChange={f("crea")} /></div>
            <div className="sm:col-span-2"><Label className="mb-1 block">Observações</Label><Textarea value={value.notes} onChange={f("notes")} rows={3} /></div>
        </div>
    );
}

export default function Processes() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [delId, setDelId] = useState(null);
    const [syncId, setSyncId] = useState(null);
    const [batchOpen, setBatchOpen] = useState(false);

    const atualizarANM = async (p) => {
        setSyncId(p.id);
        try {
            const r = await api.post(`/processes/${p.id}/scm/atualizar-dados`);
            if (r.data.status === "ok") {
                const n = r.data.deadlines_created || 0;
                toast.success(`Dados atualizados: ${p.process_number}`, { description: `Fonte: ANM / Dados Abertos (SCM).${n ? ` ${n} prazo(s) criado(s).` : ""}` });
                load();
            } else if (r.data.status === "baixando_base") {
                toast.info("Preparando a base da ANM", { description: r.data.detail });
            } else {
                toast.warning("Processo não localizado", { description: r.data.detail });
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Não foi possível atualizar os dados agora.");
        } finally { setSyncId(null); }
    };

    const load = () => api.get("/processes").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const openAdd = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
    const openEdit = (p) => {
        const merged = { ...EMPTY };
        Object.keys(EMPTY).forEach((k) => { merged[k] = p[k] ?? ""; });
        merged.area_ha = p.area_ha ?? "";
        setForm(merged); setEditId(p.id); setOpen(true);
    };

    const formKey = procKey(fullNumber(form));
    const duplicate = formKey ? items.find((p) => p.id !== editId && procKey(p.process_number) === formKey) : null;

    const submit = async () => {
        if (!form.process_number.trim()) { toast.error("Informe o número do processo."); return; }
        if (duplicate) { toast.error("Este processo já está cadastrado na sua carteira."); return; }
        if (!form.year.trim() && !form.process_number.includes("/")) {
            toast.error("Informe o ano de origem do processo (ex.: 2021).");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form, area_ha: form.area_ha ? parseFloat(form.area_ha) : null };
            if (editId) {
                await api.put(`/processes/${editId}`, payload);
                toast.success("Processo atualizado.");
            } else {
                const r = await api.post("/processes", payload);
                const n = r.data.anm_deadlines_created || 0;
                toast.success("Processo cadastrado.", n ? { description: `${n} prazo(s) criado(s) a partir das datas oficiais da ANM.` } : undefined);
            }
            setOpen(false); setForm(EMPTY); setEditId(null); load();
        } catch (e) {
            const d = e.response?.data?.detail;
            if (d && typeof d === "object") {
                toast.error(d.message || "Limite do plano atingido.", {
                    description: d.can_buy_additional
                        ? "Compre um processo adicional ou faça upgrade em Meu Plano."
                        : (d.next_plan ? `Faça upgrade para o ${d.next_plan} (até ${d.next_plan_processes} processos).` : "Faça upgrade em Meu Plano."),
                    action: { label: "Meu Plano", onClick: () => navigate("/plano") },
                });
            } else {
                toast.error(d || "Erro ao salvar.");
            }
        } finally { setSaving(false); }
    };

    const del = async () => {
        try { await api.delete(`/processes/${delId}`); toast.success("Processo excluído."); load(); }
        catch (e) { toast.error("Erro ao excluir."); }
        setDelId(null);
    };

    return (
        <div className="space-y-6" data-testid="processes-page">
            <PageHeader title="Meus Processos" subtitle="Centralize sua carteira de processos minerários."
                action={
                    <div className="flex flex-wrap gap-2">
                    <button onClick={() => setBatchOpen(true)} data-testid="batch-import-open-button"
                        className="border border-petroleum text-petroleum hover:bg-petroleum hover:text-white rounded-sm px-5 py-2.5 font-bold flex items-center gap-2 transition-colors">
                        <Upload className="w-4 h-4" /> Importar em lote
                    </button>
                    <button onClick={openAdd} data-testid="add-process-button"
                        className="bg-mineral hover:bg-mineralHover text-white rounded-sm px-5 py-2.5 font-bold shadow-md flex items-center gap-2 transition-colors">
                        <Plus className="w-4 h-4" /> Adicionar Processo
                    </button>
                    </div>
                } />

            <BatchImportDialog open={batchOpen} onOpenChange={setBatchOpen} onImported={load} />

            {items.length === 0 ? (
                <EmptyState icon={FolderKanban} title="Nenhum processo cadastrado" desc="Adicione seu primeiro processo minerário para começar a acompanhar prazos e obrigações." />
            ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {items.map((p) => (
                        <div key={p.id} className="bg-white border border-slate-200 rounded-md p-5 shadow-sm hover:shadow-md hover:border-petroleum/30 transition-all" data-testid={`process-card-${p.id}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="font-mono font-bold text-petroleum">{p.process_number}</div>
                                    <div className="text-sm text-slate-500 mt-0.5">{p.substance || "—"} · {p.municipality}/{p.uf}</div>
                                </div>
                                <div className="flex flex-col items-end gap-1.5">
                                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">{p.phase || "—"}</span>
                                    {p.dou_new_count > 0 && (
                                        <span data-testid={`dou-new-badge-${p.id}`} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-mineral/15 text-mineralHover border border-mineral/40 animate-pulse">
                                            <Satellite className="w-3 h-3" /> {p.dou_new_count === 1 ? "Novo ato" : `${p.dou_new_count} novos atos`}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="mt-4 text-xs text-slate-500 space-y-1">
                                <div>Regime: <span className="text-slate-700">{p.regime || "—"}</span></div>
                                <div>Titular: <span className="text-slate-700">{p.holder || "—"}</span></div>
                                {p.end_date && <div>Vencimento: <span className="text-slate-700 font-mono">{new Date(p.end_date).toLocaleDateString("pt-BR")}</span></div>}
                                <div data-testid={`dou-last-check-${p.id}`}>Última verificação DOU: <span className="text-slate-700">{p.monitoring_last_check ? new Date(p.monitoring_last_check).toLocaleDateString("pt-BR") : "nunca"}</span></div>
                                <div data-testid={`anm-last-sync-${p.id}`}>Última consulta ANM: <span className="text-slate-700">{p.anm_last_sync ? new Date(p.anm_last_sync).toLocaleDateString("pt-BR") : "nunca"}</span></div>
                            </div>
                            <div className="mt-4">
                                <button onClick={() => atualizarANM(p)} disabled={syncId === p.id} data-testid={`anm-update-${p.id}`}
                                    className="w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-900 text-white rounded-sm py-2 text-sm font-semibold transition-colors disabled:opacity-60">
                                    <Database className={`w-4 h-4 ${syncId === p.id ? "animate-pulse" : ""}`} /> {syncId === p.id ? "Atualizando…" : "Atualizar dados ANM"}
                                </button>
                            </div>
                            <div className="mt-2">
                                <button onClick={() => navigate(`/diagnostico-anm?process=${p.id}`)} data-testid={`diagnose-process-${p.id}`}
                                    className="w-full flex items-center justify-center gap-2 bg-mineral/10 hover:bg-mineral hover:text-white text-mineralHover rounded-sm py-2 text-sm font-semibold transition-colors">
                                    <Stethoscope className="w-4 h-4" /> Analisar com IA
                                </button>
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                                <button onClick={() => navigate(`/processos/${p.id}`)} data-testid={`view-process-${p.id}`}
                                    className="flex-1 flex items-center justify-center gap-2 bg-petroleum/10 hover:bg-petroleum hover:text-white text-petroleum rounded-sm py-2 text-sm font-semibold transition-colors">
                                    <Eye className="w-4 h-4" /> Abrir
                                </button>
                                <button onClick={() => openEdit(p)} data-testid={`edit-process-${p.id}`} className="p-2 text-slate-400 hover:text-petroleum transition-colors" title="Editar"><Pencil className="w-4 h-4" /></button>
                                <button onClick={() => setDelId(p.id)} data-testid={`delete-process-${p.id}`} className="p-2 text-slate-400 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Disclaimer>Plano Profissional permite até 3 processos ANM. Os dados inseridos são de responsabilidade do usuário.</Disclaimer>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle className="font-heading">{editId ? "Editar Processo" : "Adicionar Processo"}</DialogTitle></DialogHeader>
                    <ProcessForm value={form} onChange={setForm} duplicate={duplicate} />
                    <DialogFooter>
                        <button onClick={() => setOpen(false)} className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm font-medium">Cancelar</button>
                        <button onClick={submit} disabled={saving || !!duplicate} data-testid="save-process-button" className="bg-petroleum hover:bg-petroleumHover text-white rounded-sm px-6 py-2 font-semibold transition-colors disabled:opacity-60">{saving ? "Salvando..." : "Salvar"}</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Excluir processo?</AlertDialogTitle>
                        <AlertDialogDescription>Esta ação removerá o processo, seus prazos e movimentações. Não pode ser desfeita.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-process">Excluir</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
